import { Component, DestroyRef, Input, OnInit, inject } from '@angular/core';
import { PreorderType } from '../../../smoothr-web-app-core/enums/PreorderType';
import moment from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { RepositoryService } from '../../../smoothr-web-app-core/services/repository/repository.service';
import { AlertController, ModalController } from '@ionic/angular';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import RepositoryInterface from '../../../smoothr-web-app-core/directives/repository-directive';
import { OrderListAction } from '../../enums/OrderListAction';
import { AppComponent } from '../../app.component';
import { ModalInfoComponent } from '../modal-info/modal-info.component';
import { CheckoutModalComponent } from '../checkout-modal/checkout-modal.component';
import { OrderUtils } from '../../../smoothr-web-app-core/utils/order-utils';
import Article from '../../../smoothr-web-app-core/models/Article';
import ArticleOption from '../../../smoothr-web-app-core/models/ArticleOption';
import { MenuPage } from '../../pages/menu/menu.page';
import Order from '../../../smoothr-web-app-core/models/Order';
import { PromoCodeType } from '../../../smoothr-web-app-core/models/PromoCodeType';
import {
	defaultsToArticleOption,
	numberD,
	numberToCurrency,
	sendOrder,
	sleep,
} from '../../../smoothr-web-app-core/utils/utils';
import { AnalyticsService } from '../../../smoothr-web-app-core/services/analytics/analytics.service';
import { debounceTime } from 'rxjs/operators';
import ArticleGroup from 'src/smoothr-web-app-core/models/ArticleGroup';
import { ValidationUtils } from 'src/smoothr-web-app-core/utils/validation-utils';
import OptionGroup from 'src/smoothr-web-app-core/models/OptionGroup';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import Localization from 'src/smoothr-web-app-core/models/Localization';
import { environment } from 'src/environments/environment';
import { OrderErrorModalComponent } from '../order-error-modal/order-error-modal.component';
import { Api } from 'src/smoothr-web-app-core/api/api';

@Component({
	selector: 'app-order-content',
	templateUrl: './order-content.component.html',
	styleUrls: ['order-content.component.scss'],
	standalone: false,
})
export class OrderContentComponent extends RepositoryInterface implements OnInit {
	moment = moment;

	isValid = false;
	loading = false;

	ou = OrderUtils;
	pt = PreorderType;
	differenceToMvo: number;
	@Input()
	fromMenu = false;
	promoError: string;
	tip = 0;
	recommendationArticles: Article[] = [];
	numberToCurrency = numberToCurrency;

	slideOptions = {
		spaceBetween: 0,
		initialSlide: 0,
		speed: 400,
		allowTouchMove: true,
		// loop: true,
		autoplay: true,
		slidesPerView: 3,
		zoom: false,
		preloadImages: true,
	};

	private translate = inject(TranslateService);
	private modalCtrl = inject(ModalController);
	private snackbarCtrl = inject(MatSnackBar);
	private router = inject(Router);
	private route = inject(ActivatedRoute);
	private alertCtrl = inject(AlertController);
	private analytics = inject(AnalyticsService);
	protected repository = inject(RepositoryService);

	constructor() {
		super();
		this.validate();
	}

	get hasArticles(): boolean {
		return !!this.order && this.order.orderedArticles.length > 0;
	}

	ngOnInit() {
		super.ngOnInit();
		this.analytics.openOrderContent();

		this.repository.recommendationList$
			.pipe(debounceTime(500), takeUntilDestroyed(this.destroyRef))
			.subscribe(recommendationArt => {
				if (this.venue && recommendationArt.length > 0) {
					this.recommendationArticles = [];
					recommendationArt = recommendationArt.map(it => {
						// it.articles = it.articles.splice(0, 2);
						return it;
					});
					const categoryArticles = [].concat(...this.venue.articleCategories.map(it => it.articles)) as Article[];
					const categoryArticlesMap = new Map(categoryArticles.map(it => [it._id, it]));
					recommendationArt.forEach(it => {
						it.articles.forEach(art => {
							if (categoryArticlesMap.has(art)) {
								this.recommendationArticles.push(categoryArticlesMap.get(art));
							}
						});
					});
					// this.startAutoPlaySlider();
				} else {
					this.recommendationArticles = [];
				}
			});
	}

	onVenue() {
		super.onVenue();
		this.validate();
	}

	onOrder() {
		super.onOrder();
		if (this.order && this.order.orderedArticles && this.order.orderedArticles.length > 0) {
			this.analytics.orderHasProducts();
		}
		this.validate();
	}

	async onAction(index: number, event: OrderListAction) {
		switch (event) {
			case OrderListAction.add:
				this.order.orderedArticles[index].quantity += 1;
				break;
			case OrderListAction.delete:
				if (OrderUtils.isBogoOrFreeArticlePromo(this.order) && this.order.promoCode.type === PromoCodeType.BOGO) {
					const bogoArticleIndex = this.order.orderedArticles.findIndex(ag => ag.isPromo);
					const bogoArticle = this.order.orderedArticles[bogoArticleIndex];
					const matchingBogoArticleIndices = this.order.orderedArticles
						.map((ag, agIndex) => {
							return ag.article._id === bogoArticle.article._id &&
								!ag.isPromo &&
								OrderUtils.bogoPrice(ag, this.order.type, this.order.preorder.type) ===
									OrderUtils.bogoPrice(bogoArticle, this.order.type, this.order.preorder.type)
								? agIndex
								: -1;
						})
						.filter(agIndex => agIndex !== -1);
					if (matchingBogoArticleIndices.find(bogoIndex => bogoIndex === index) !== undefined) {
						// article that should be removed is a possible parent of promoCode article
						if (matchingBogoArticleIndices.length === 1) {
							// only that article is possible parent of promoCode. If this article is removed the PromoCode and PromoArticle
							// should also be removed. Ask user.
							const bogoAlert = await this.alertCtrl.create({
								header: this.translate.instant('remove_bogo_alert.header'),
								message: this.translate.instant('remove_bogo_alert.message'),
								buttons: [
									{
										text: this.translate.instant('remove_bogo_alert.button_remove'),
										handler: () => {
											this.order.orderedArticles[bogoArticleIndex].isPromo = false;
											this.order.promoCode = null;
											this.order.orderedArticles.splice(index, 1);
											bogoAlert.dismiss();
										},
									},
									{
										text: this.translate.instant('remove_bogo_alert.button_keep'),
										handler: () => {
											bogoAlert.dismiss();
										},
									},
								],
							});
							await bogoAlert.present();
							await bogoAlert.onDidDismiss();
							break;
						}
					}
				}
				this.order.orderedArticles.splice(index, 1);
				break;
			case OrderListAction.note:
				this.noteArticle(index);
				break;
			case OrderListAction.edit:
				const modal = await this.modalCtrl.create({
					cssClass: AppComponent.largeScreen ? 'item-modal large-modal' : 'item-modal',
					component: ModalInfoComponent,
					componentProps: {
						// copy article group
						articleGroup: structuredClone(this.order.orderedArticles[index]),
						recommend: false,
					},
					showBackdrop: true,
					backdropDismiss: true,
				});
				await modal.present();
				const response = await modal.onDidDismiss();
				if (response.data && response.data.articleGroup) {
					const promoAgIndex = this.order.orderedArticles.findIndex(oa => oa.isPromo);
					if (
						OrderUtils.isBogoOrFreeArticlePromo(this.order) &&
						this.order.promoCode.type === PromoCodeType.BOGO &&
						this.order.orderedArticles[index].article._id === this.order.orderedArticles[promoAgIndex].article._id &&
						OrderUtils.bogoPrice(response.data.articleGroup, this.order.type, this.order.preorder.type) <
							OrderUtils.bogoPrice(this.order.orderedArticles[promoAgIndex], this.order.type, this.order.preorder.type)
					) {
						this.order.orderedArticles[promoAgIndex].isPromo = false;
						const newPromoAg = structuredClone(response.data.articleGroup);
						newPromoAg.isPromo = true;
						this.order.orderedArticles.push(newPromoAg);
					}
					this.order.orderedArticles[index] = response.data.articleGroup;
					this.repository.order.emit(this.order);
				}
				break;
			case OrderListAction.remove:
				if (this.order.orderedArticles[index].quantity > 1) {
					this.order.orderedArticles[index].quantity -= 1;
				} else {
					await this.onAction(index, OrderListAction.delete);
					return;
				}
				break;
		}
		this.repository.order.emit(this.order);
	}

	async openCheckoutModal() {
		this.validate();
		if (!this.isValid || this.loading) {
			return;
		}
		this.loading = true;

		const res = await this.cassaValidation(this.order);

		if (!res) {
			this.loading = false;
			return;
		}

		const result = await CheckoutModalComponent.show(this.modalCtrl, this.analytics, this.order, this.tip);
		console.log(result);
		if (result.data === undefined) {
			this.loading = false;
			return;
		}
		this.order.orderAt = result.data.orderAt;
		this.order.preorder = result.data.preorder;
		if (!this.order.preorder.name) {
			this.order.preorder.name = 'defaultName';
		}
		if (!this.order.preorder.email) {
			this.order.preorder.email = 'defaultName@smoothr.de';
		}

		this.order.cardNumber = result.data.cardNumber;
		this.tip = result.data.tip;
		console.log(this.tip);
		this.repository.order.emit(this.order);
		console.log(this.order);
		try {
			await sendOrder(
				this.router,
				this.modalCtrl,
				this.translate,
				this.repository,
				this.order,
				this.tip,
				loading => (this.loading = loading),
				this.snackbarCtrl,
				this.order.tableNumber !== 'preorder_table'
			);
		} catch (e) {
			console.log(structuredClone(e));
		}
	}

	async cassaValidation(order: Order): Promise<Boolean> {
		const copyOrder = structuredClone(order);
		this.order.totalPrice = '';
		this.order.checkoutTotalDiff = '';

		if (copyOrder.preorder && !copyOrder.preorder.name) {
			copyOrder.preorder.name = 'defaultName';
		}
		if (copyOrder.preorder && !copyOrder.preorder.email) {
			copyOrder.preorder.email = 'defaultName@smoothr.de';
		}

		if (!copyOrder.userUid) {
			copyOrder.userUid = this.customer.uid;
		}

		copyOrder.asap = true;

		try {
			const testOrder = (await Api.createOrder(copyOrder, false)).data;

			// if (Number(testOrder.checkoutTotalDiff) !== 0) {
			// 	const res = await RecalculationPriceModalComponent.show(this.modalCtrl, testOrder);
			// 	this.order.totalPrice = testOrder.totalPrice;
			// 	this.order.checkoutTotalDiff = testOrder.checkoutTotalDiff;

			// 	this.repository.order.emit(this.order);

			// 	return res;
			// }

			return true;
		} catch (error: any) {
			const errData = error?.response?.data;

			if (!errData) {
				console.error('UNKNOWN ERROR:', error);
				return false;
			}

			try {
				const jsonMatch = errData.message.match(/{.*}/);
				// if (!jsonMatch) return false;

				const parsed = JSON.parse(jsonMatch[0]);

				const posError = parsed?.posDetails?.[0];
				if (!posError?.message) {
					throw Error("Unknown error - 'posError' ");
				}

				const match = posError.message.match(/(\d+)/);
				const menuItemId = match ? match[1] : null;

				if (menuItemId) {
					const matchedArticle = order.orderedArticles
						.map(articleGroup => articleGroup.article)
						.concat(
							order.orderedArticles.flatMap(orderArticle =>
								orderArticle.groups.flatMap(articleOption => articleOption.article)
							)
						)
						.find(article => article.number === menuItemId || article.optionNumber === menuItemId);

					if (!matchedArticle) {
						console.error('Article not found', menuItemId);
						throw Error("Unknown error - 'matchedArticle' ");
					}

					const mainArticle = order.orderedArticles.find(articleGroup =>
						articleGroup.groups.find(group => group.article.optionNumber === menuItemId)
					);

					const text = this.translate.instant('order_error.article_not_found', {
						name: (mainArticle ? this.getName(mainArticle?.article) + ', ' : '') + this.getName(matchedArticle),
					});

					await OrderErrorModalComponent.show(this.modalCtrl, '', text);

					const foundArticleGroup = order.orderedArticles.find(
						articleGroup =>
							articleGroup.article._id === matchedArticle._id ||
							articleGroup.groups.find(group => group.article._id === matchedArticle._id)
					);

					if (!foundArticleGroup) {
						console.error('Article not found', menuItemId);
						throw Error("Unknown error - 'foundArticleGroup' ");
					}

					const index = this.order.orderedArticles.indexOf(foundArticleGroup);
					if (index > -1) {
						this.order.orderedArticles.splice(index, 1);
					}
					this.repository.order.emit(this.order);
					return false;
				}
			} catch (e) {
				console.error('Unknown error checkout integration error:', errData);
			}

			this.snackbarCtrl.open(
				this.translate.instant('axios_errors.400', {
					support_email: environment.SUPPORT_EMAIL,
				}),
				undefined,
				{
					duration: 4000,
				}
			);

			return false;
		}
	}

	getName(art: Article) {
		return art.name[this.translate.currentLang as keyof Localization] || art.name.en || art.name.de;
	}

	validate() {
		if (!this.order || !this.venue || !this.order.preorder) {
			this.isValid = false;
			return;
		}
		const result = OrderUtils.validateOrder(this.venue, this.order);
		this.differenceToMvo = result.movDifference;
		this.isValid = result.valid;
		console.log(result);
	}

	totalPrice(): string {
		return numberToCurrency(OrderUtils.orderTotalPrice(this.order, true, true) + this.tip, this.order.currency);
	}

	replace(index: number, article: Article, options: ArticleOption[]) {
		this.order.orderedArticles[index].quantity = 1;
		this.order.orderedArticles[index].article = article;
		this.order.orderedArticles[index].groups = options;
	}

	onPromoApplied(order: Order) {
		this.promoError = null;
		this.repository.order.emit(order);
	}

	onDeletePromo(order: Order) {
		this.promoError = null;
		this.repository.order.emit(order);
	}

	onPromoCodeError(error: string) {
		if (!error) {
			// error just resetting error no error happened
			return;
		}
		// Promo error
		this.snackbarCtrl.open(error, null, { duration: 2000 });
		this.repository.order.emit(OrderUtils.removePromo(this.order));
		this.promoError = error;
	}

	backToMenu() {
		MenuPage.navigate(this.router);
	}
	async noteArticle(index: number) {}

	async addArticle(article: Article) {
		const articleGroup = new ArticleGroup();
		articleGroup.article = article;
		articleGroup.groups.push(...defaultsToArticleOption(article, [], article.defaults, this.order.preorder.type));
		articleGroup.quantity = 1;
		// const modal = await this.modalCtrl.create({
		// 	cssClass: AppComponent.largeScreen ? 'item-modal large-modal' : 'item-modal',
		// 	component: ModalInfoComponent,
		// 	componentProps: {
		// 		articleGroup,
		// 		showOnlyRequiredGroups: true,
		// 	},
		// 	mode: 'ios',
		// 	backdropDismiss: true,
		// });
		// await modal.present();
		// const response = await modal.onDidDismiss();
		// if (response.data && response.data.articleGroup) {
		// 	OrderUtils.addToOrder(this.order, response.data.articleGroup, this.analytics);

		// 	this.repository.order.next(this.order);
		// 	await sleep(300);
		// }
		OrderUtils.addToOrder(this.order, articleGroup, this.analytics);
		this.repository.order.next(this.order);
		await sleep(300);
	}

	calcArticlePrice(art: Article) {
		if (!this.order || !art) {
			return '';
		}
		return this.reloadPrice(art, this.order);
	}

	private reloadPrice(articleValue: Article, order: Order) {
		const articleGroup = new ArticleGroup();
		articleGroup.article = articleValue;
		articleGroup.groups = defaultsToArticleOption(articleValue, [], [], order.preorder.type);

		articleGroup.quantity = 1;

		const article = articleGroup.article;
		let price = numberD(article.price);
		const processedGroups: string[] = [];
		article.groups.forEach(grp => {
			if (processedGroups.indexOf(grp._id) >= 0) return;
			if (ValidationUtils.isGroupDependencyFulfilled(articleGroup.article, articleGroup.groups, grp).times < 0) return;
			if (grp.requiredAmount == grp.limit) {
				const minimumItem = this.minOfItem(grp.articles, opt => numberD(opt.price));

				price += numberD(minimumItem.price) * grp.requiredAmount;
				const dependants = this.dependenciesRegardingOptionArticle(article, article.groups, minimumItem);
				dependants.forEach(dependant => {
					const dependantMinimumItem = this.minOfItem(dependant.articles, opt => numberD(opt.price));
					price += grp.requiredAmount * dependant.requiredAmount * numberD(dependantMinimumItem.price);
					processedGroups.push(dependant._id);
				});
				processedGroups.push(grp._id);
			}
		});
		return numberToCurrency(price, this.order.currency);
	}

	private minOfItem<T>(items: T[], transformer: (item: T) => number): T {
		if (items.length === 1) {
			return items[0];
		}
		const values = items.map(item => transformer(item));
		const min = Math.min(...values);
		const index = values.indexOf(min);
		return items[index];
	}

	dependenciesRegardingOptionArticle(article: Article, groups: OptionGroup[], optionArticle: Article): OptionGroup[] {
		return article.groupDependencies
			.map(gd => {
				if (gd.dependencies.find(dependeci => dependeci.groupArticles.includes(optionArticle._id)) == null) {
					return null;
				} else {
					return groups.find(it => gd.group === it._id);
				}
			})
			.filter(it => !!it);
	}
}
